import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// In CRA dev builds, the webpack-dev-server runtime error overlay pops over
// the whole app for *recovered* React rendering errors (react-quill-new +
// React 19 trigger one on mount). React re-renders synchronously and the app
// keeps working — so swallow the overlay so users can actually interact with
// the UI. Production builds are unaffected.
if (process.env.NODE_ENV !== "production") {
  const hideOverlay = () => {
    document.querySelectorAll(
      'iframe#webpack-dev-server-client-overlay, iframe[src*="webpack-dev-server"], div[data-react-error-overlay]'
    ).forEach((el) => { el.style.display = "none"; });
  };
  const mo = new MutationObserver(hideOverlay);
  mo.observe(document.documentElement, { childList: true, subtree: true });
  hideOverlay();
}

// -------------------------------------------------------------------------
// Greek accent stripper for uppercased text
// Modern Greek typography omits the tonos on capitals (e.g. "ΣΗΜΕΙΟ" not
// "ΣΗΜΕΊΟ"). Browser `text-transform: uppercase` keeps the tonos, which looks
// unidiomatic. We walk any element whose COMPUTED style is uppercase and
// rewrite its text nodes once. A MutationObserver re-runs on DOM changes.
// -------------------------------------------------------------------------
(() => {
  const GR_TONES_LOWER = { 'ά': 'α', 'έ': 'ε', 'ή': 'η', 'ί': 'ι', 'ό': 'ο', 'ύ': 'υ', 'ώ': 'ω', 'ϊ': 'ι', 'ϋ': 'υ', 'ΐ': 'ι', 'ΰ': 'υ' };
  const GR_TONES_UPPER = { 'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω', 'Ϊ': 'Ι', 'Ϋ': 'Υ' };
  const TONE_RE = /[ΆΈΉΊΌΎΏΪΫάέήίόύώϊϋΐΰ]/;
  const strip = (s) => s.replace(/[ΆΈΉΊΌΎΏΪΫάέήίόύώϊϋΐΰ]/g, (c) => GR_TONES_UPPER[c] || GR_TONES_LOWER[c] || c);

  const isUppercased = (el) => {
    try {
      const cs = window.getComputedStyle(el);
      return cs.textTransform === 'uppercase';
    } catch { return false; }
  };

  const processElement = (el) => {
    if (!el || el.nodeType !== 1) return;
    if (!isUppercased(el)) return;
    // Walk only immediate text nodes + inline text descendants that don't
    // themselves override text-transform.
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!TONE_RE.test(node.nodeValue)) continue;
      node.nodeValue = strip(node.nodeValue);
    }
  };

  const scan = (root) => {
    if (!root || (root.nodeType !== 1 && root.nodeType !== 9)) return;
    const els = root.querySelectorAll ? root.querySelectorAll('*') : [];
    els.forEach(processElement);
  };

  const run = () => {
    scan(document.body);
  };

  // Initial + after-load passes
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Debounced observer — batch mutations so we don't run per keystroke.
  let scheduled = false;
  const mo2 = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      run();
    });
  });
  mo2.observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
  });
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
// StrictMode disabled in dev because it double-invokes effects and causes
// spurious "concurrent rendering" overlays with our authFetch + XHR paths.
root.render(<App />);
